import type { LockContext } from '@powersync/common';

import type { DemoRow, DemoRowValues, DemoTableName } from './tables';

/** What the demo needs from a PowerSync database or transaction. */
export type DemoDb = Pick<
  LockContext,
  'execute' | 'get' | 'getAll' | 'getOptional'
>;

// Read helpers mirror WatermelonDB's non-optional columns, which came back as
// '' / 0 / false instead of null.
export const readText = (value: unknown): string =>
  value == null ? '' : String(value);

export const readNumber = (value: unknown): number =>
  value == null ? 0 : Number(value);

export const readBool = (value: unknown): boolean => Boolean(value);

export const toSqlBool = (value: unknown): number => (value ? 1 : 0);

export function parseJsonArray<T>(value: unknown, fallback: T[] = []): T[] {
  if (typeof value !== 'string' || !value) {
    return fallback;
  }
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : fallback;
  } catch {
    return fallback;
  }
}

export function parseJsonObject(value: unknown): Record<string, unknown> {
  if (typeof value !== 'string' || !value) {
    return {};
  }
  try {
    const parsed = JSON.parse(value);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed;
    }
    return {};
  } catch {
    return {};
  }
}

function bindValues(values: Record<string, unknown>): {
  columns: string[];
  params: (string | number | null)[];
} {
  const columns = Object.keys(values);
  // SQLite cannot bind undefined; store it as null like an unset column.
  const params = columns.map(
    name => (values[name] ?? null) as string | number | null,
  );
  return { columns, params };
}

/** Throws like WatermelonDB's `find()` when the row is missing. */
export function getDemoRow<T extends DemoTableName>(
  db: DemoDb,
  table: T,
  id: string,
): Promise<DemoRow<T>> {
  return db.get<DemoRow<T>>(`SELECT * FROM ${table} WHERE id = ?`, [id]);
}

/** Inserts a row with a new `uuid()` id and returns that id. */
export async function insertDemoRow<T extends DemoTableName>(
  db: DemoDb,
  table: T,
  values: DemoRowValues<T>,
): Promise<string> {
  const { id } = await db.get<{ id: string }>('SELECT uuid() AS id');
  const { columns, params } = bindValues(values);
  const names = ['id', ...columns];
  await db.execute(
    `INSERT INTO ${table} (${names.join(', ')}) VALUES (${names
      .map(() => '?')
      .join(', ')})`,
    [id, ...params],
  );
  return id;
}

/**
 * Updates one row; throws when it is missing. PowerSync views may report
 * rowsAffected 0 for a successful update, so the row is checked first.
 */
export async function updateDemoRow<T extends DemoTableName>(
  db: DemoDb,
  table: T,
  id: string,
  values: DemoRowValues<T>,
): Promise<void> {
  await getDemoRow(db, table, id);
  const { columns, params } = bindValues(values);
  await db.execute(
    `UPDATE ${table} SET ${columns
      .map(name => `${name} = ?`)
      .join(', ')} WHERE id = ?`,
    [...params, id],
  );
}

/** Deletes one row; throws when it is missing. */
export async function deleteDemoRow(
  db: DemoDb,
  table: DemoTableName,
  id: string,
): Promise<void> {
  await getDemoRow(db, table, id);
  await db.execute(`DELETE FROM ${table} WHERE id = ?`, [id]);
}
